import { SignalStoreFeature } from '@ngrx/signals';
import { SignalPropertyDefinition, SYM_SIGNAL_STATE_PROPERTIES } from './signal-state-property.decorator';
import { Signal, Type, signal } from '@angular/core';

export function SignalStateService<T extends Constructor<any>>(
  options: SignalStateServiceOptions = { decorateAll: true, debug: false },
): (cls: T) => WithSignalSuffix<T> {
  return function (cls: T): WithSignalSuffix<T> {
    function logger(...args: unknown[]) {
      if (options.debug) {
        console.debug(`SignalStateService: ${cls.name}`, ...args);
      }
    }
    const signalProperties = prepareProperties(cls, options);
    logger(`SignalStateService: Preparing properties for ${cls.name}`, signalProperties);

    if (!signalProperties || signalProperties.length === 0) {
      return cls as unknown as WithSignalSuffix<T>; // this is technically not correct
    }
    signalProperties.forEach(definition => {
      const { propertyKey } = definition;
      const internalSignalKey = `${propertyKey.toString()}Signal`;
      const descriptor = getPropertyDescriptorInPrototypeChain(cls.prototype, propertyKey);
      if (descriptor.configurable === false) {
        logger(
          `SignalStateService: Property "${propertyKey.toString()}" in class "${cls.name}" is not configurable. Skipping signal wrapping.`,
        );
        return;
      }
      if (!descriptor) {
        logger(`SignalStateService: Property "${propertyKey.toString()}" in class "${cls.name}" not found. Skipping signal wrapping.`);
        return;
      }
      if (descriptor.value && typeof descriptor.value === 'function') {
        logger(`SignalStateService: Property "${propertyKey.toString()}" in class "${cls.name}" is a function. Skipping signal wrapping.`);
        return;
      }
      defineInternalSignalProperty(cls.prototype, internalSignalKey, descriptor);
      wrapOriginalProperty(cls.prototype, internalSignalKey, propertyKey, descriptor);
    });
    return cls as unknown as WithSignalSuffix<T>;
  };
}

function defineInternalSignalProperty<T, K extends string>(target: T, internalSignalKey: K, descriptor: PropertyDescriptor) {
  Object.defineProperty(target, internalSignalKey, {
    value: undefined,
    writable: true,
    enumerable: descriptor.enumerable,
    configurable: descriptor.configurable,
  });
}

export function invalidateSignals<T>(service: T, properties: (keyof T)[] = undefined): void {
  const props = ((service[SYM_SIGNAL_STATE_PROPERTIES] ?? []) as SignalPropertyDefinition[]).filter(p =>
    properties ? properties.includes(p.propertyKey as unknown as keyof T) : true,
  );
  for (const prop of props) {
    prop.invalidate?.call(service);
  }
}

function wrapOriginalProperty<T, K extends keyof T>(target: T, internalSignalKey: K, propertyKey: K, descriptor: PropertyDescriptor) {
  const originalGetter = descriptor.get;
  const originalSetter = descriptor.set;
  Object.defineProperty(target, propertyKey, {
    get: function () {
      if (this[internalSignalKey] === undefined) {
        // initialize the signal from the default getter
        this[internalSignalKey] = signal(originalGetter ? originalGetter.call(this) : descriptor.value);
      }
      return this[internalSignalKey](); // access the signal value (will also subscribe in reactive contexts)
    },
    set: function (value: any) {
      if (originalSetter) {
        // call the original setter if it exists so we can keep the original behavior. i.E. SessionStorage
        originalSetter.call(this, value);
      }
      if (this[internalSignalKey] === undefined) {
        // initialize the signal from the default getter if it hasn't been initialized yet
        this[internalSignalKey] = signal(originalGetter ? originalGetter.call(this) : descriptor.value);
      }
      if (originalSetter) {
        // because the setter might have changed the value, we need to update the signal from the getter
        this[internalSignalKey].set(originalGetter ? originalGetter.call(this) : value);
      } else {
        // if we have no original setter, we just set the value as is
        this[internalSignalKey].set(value);
      }
    },
  });
  // add invalidate function to SYM_SIGNAL_STATE_PROPERTIES if it exists
  const signalProperties = target[SYM_SIGNAL_STATE_PROPERTIES] ?? ([] as SignalPropertyDefinition[]);
  signalProperties.push({
    propertyKey,
    invalidate: function () {
      if (this[internalSignalKey]) {
        this[internalSignalKey].set(originalGetter ? originalGetter.call(this) : descriptor.value);
      }
    },
  });
  target[SYM_SIGNAL_STATE_PROPERTIES] = signalProperties;
}

export type WithSignalSuffix<T, K extends keyof T = keyof T> = T & {
  // For all properties that are not functions, add a property with a Signal suffix
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  [P in K as T[P] extends Function ? never : `${Extract<P, string>}Signal`]: Signal<T[P]>;
};
type SignalStateServiceOptions = {
  features?: SignalStoreFeature[] /** Additional features for the resulting store */;
  decorateAll?: boolean /** Only modify decorated properties (SignalStateProperty()) */;
  debug?: boolean /** Enable debug mode to log changes to the console */;
};
type Constructor<T> = new (...args: any[]) => T;

// get the property descriptor by traversing the prototype chain (for inherited properties)
function getPropertyDescriptorInPrototypeChain<T>(c: T, propertyKey: string | symbol) {
  let descriptor;
  do {
    descriptor = Object.getOwnPropertyDescriptor(c, propertyKey);
    c = Object.getPrototypeOf(c);
  } while (c && !descriptor);
  return descriptor;
}

function prepareProperties<T>(cls: Type<T>, options: SignalStateServiceOptions) {
  let signalProperties = cls.prototype.constructor[SYM_SIGNAL_STATE_PROPERTIES] as SignalPropertyDefinition[];
  if (options.decorateAll) {
    // append all properties that are not functions
    signalProperties = [
      ...(signalProperties ?? []),
      ...getPropertyNamesInPrototypeChain(cls.prototype)
        .filter(propertyKey => {
          // skip properties already defined in singalProperties
          if (signalProperties?.find(sp => sp.propertyKey === propertyKey)) {
            return false;
          }
          // only non function properties
          const descriptor = Object.getOwnPropertyDescriptor(cls.prototype, propertyKey);
          return descriptor && typeof descriptor.value !== 'function';
        })
        .map(propertyKey => ({ propertyKey })),
    ];
  }
  cls[SYM_SIGNAL_STATE_PROPERTIES] = signalProperties;
  return signalProperties;
}

function getPropertyNamesInPrototypeChain(obj: object) {
  const properties = new Set<string>();
  let c = obj;
  do {
    Object.getOwnPropertyNames(c).forEach(p => properties.add(p));
    c = Object.getPrototypeOf(c);
  } while (c);
  return Array.from(properties);
}
